import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import {
  WORKSPACE_HOME_TOOLS,
  WORKSPACE_HOME_CASES,
} from "@/constants/workspaceHomeCards";
import { HOME } from "@/constants/testIds/home";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function WorkspaceHome() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const displayName = (user?.name || user?.email || "").trim();
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;
  const CasesIcon = WORKSPACE_HOME_CASES.icon;

  useEffect(() => {
    const tab = location.state?.tab;
    if (tab === "search" || tab === "library") {
      navigate("/workspace/research", { replace: true });
    } else if (tab === "chat" || tab === "draft") {
      navigate("/workspace/cases", { replace: true });
    } else if (tab && ["research", "cases"].includes(tab)) {
      navigate(`/workspace/${tab}`, { replace: true });
    }
  }, [location.state, navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid={HOME.page}>
      <Header
        activeTab={null}
        onTabChange={(tab) => navigate(`/workspace/${tab}`)}
      />

      <main className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
          <div className="mb-10" data-testid={HOME.welcome}>
            <div className="eyebrow mb-3">{t("appTagline")}</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl text-slate-900 tracking-tight mb-3">
              {displayName ? `${t("home_welcome")}, ${displayName}` : t("home_welcome")}
            </h1>
            <p className="text-slate-600 leading-relaxed max-w-2xl">{t("home_sub")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
              {WORKSPACE_HOME_TOOLS.map((card) => {
                const Icon = card.icon;
                return (
                  <button
                    key={card.section}
                    type="button"
                    data-testid={HOME.card(card.section)}
                    onClick={() => navigate(`/workspace/${card.section}`)}
                    className="group bg-white p-8 lg:p-9 text-start hover:bg-slate-50 transition-colors duration-150"
                  >
                    <div className="w-11 h-11 grid-border flex items-center justify-center mb-5 text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors duration-150">
                      <Icon size={18} strokeWidth={1.5} />
                    </div>
                    <h2 className="font-serif-display text-xl text-slate-900 mb-2">
                      {t(card.titleKey)}
                    </h2>
                    <p className="text-sm text-slate-600 leading-relaxed mb-5 line-clamp-3">
                      {t(card.descKey)}
                    </p>
                    <span className="inline-flex items-center gap-2 text-xs font-mono-tight uppercase tracking-wider text-slate-900">
                      {t("home_open")}
                      <Arrow size={12} />
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                data-testid={HOME.card(WORKSPACE_HOME_CASES.section)}
                onClick={() => navigate(`/workspace/${WORKSPACE_HOME_CASES.section}`)}
                className="group bg-slate-900 text-white p-8 lg:p-9 text-start hover:bg-slate-800 transition-colors duration-150"
              >
                <div className="w-11 h-11 grid-border border-white/20 flex items-center justify-center mb-5 text-white group-hover:bg-white group-hover:text-slate-900 transition-colors duration-150">
                  <CasesIcon size={18} strokeWidth={1.5} />
                </div>
                <div className="eyebrow !text-white/50 mb-2">
                  {t(WORKSPACE_HOME_CASES.eyebrowKey)}
                </div>
                <h2 className="font-serif-display text-xl text-white mb-2">
                  {t(WORKSPACE_HOME_CASES.titleKey)}
                </h2>
                <p className="text-sm text-white/60 leading-relaxed mb-5 line-clamp-3">
                  {t(WORKSPACE_HOME_CASES.descKey)}
                </p>
                <span className="inline-flex items-center gap-2 text-xs font-mono-tight uppercase tracking-wider text-white">
                  {t("home_cases_open")}
                  <Arrow size={12} />
                </span>
              </button>
          </div>
        </div>
      </main>
    </div>
  );
}
